import { UnitType, WasteClassification } from "generated-graphql/graphql";

import { DisplayLine } from "./useStagedShipmentLines";

/**
 * Whether a row is a real shipment line.
 *
 * The grid calls `getActions` for auto-generated group rows as well, and their
 * `row` is a synthetic object carrying only the grouping key. `renderCell` can
 * ask `rowNode.type` instead, but `GridRowParams` carries no node, so this
 * checks the row's own shape.
 */
export function isDisplayLine(row: unknown): row is DisplayLine {
  return typeof row === "object" && row !== null && "container" in row;
}

/**
 * Every container with no stream shares one group, rather than one group each.
 * `groupLinesByStream` keys them per line so two deleted streams don't collapse
 * together; in a grid one "Uncharacterized" heading reads better than a run of
 * identical ones, and it matches how the manifest request already buckets them.
 */
export const UNCHARACTERIZED_NAME = "Uncharacterized";

/**
 * Separates the two halves of a grouping key. A control character so it can
 * never occur inside a stream name and split one by accident.
 */
const KEY_SEPARATOR = "\u0000";

/**
 * The value the grid groups on: the stream's name, then its id.
 *
 * Both halves earn their place. The grid sorts groups by this value, so leading
 * with the name is what makes sorting the column order streams alphabetically —
 * keyed on the id alone it sorted by UUID, which reordered the table into an
 * arbitrary order that looks alphabetical until you have enough streams to
 * notice it isn't. The id still trails it because two streams may share a name
 * and the group has to stay one stream.
 */
export function streamKeyFor(
  streamName: string | null | undefined,
  streamId: string | null
): string {
  return `${streamName ?? UNCHARACTERIZED_NAME}${KEY_SEPARATOR}${streamId ?? ""}`;
}

export function streamKeyOf(line: DisplayLine): string {
  return streamKeyFor(line.wasteStream?.name, line.wasteStream?.id ?? null);
}

/**
 * The order the table reads in: streams alphabetically with uncharacterized
 * last, and containers by label within each one — the order the manifest request
 * groups them in, so the internal view and the vendor's copy agree.
 *
 * Done by ordering the rows because the grid sets no sort model, and MUI takes a
 * group's position from where its first row appears. Left to itself the grid
 * inherited the API's `createdAt asc`, which listed streams in whatever order
 * the shipment happened to be packaged in, let Uncharacterized land mid-table,
 * and put a later-added container at the foot of its stream whatever its label.
 *
 * Sorting on the whole grouping key rather than the name alone keeps two
 * same-named streams from interleaving into each other's headings.
 */
export function orderedForDisplay<
  TLine extends {
    wasteStream?: { id: string; name: string } | null;
    container: { containerLabel: string };
  },
>(lines: TLine[]): TLine[] {
  return [...lines].sort((a, b) => {
    const aUncharacterized = a.wasteStream?.name == null;
    const bUncharacterized = b.wasteStream?.name == null;
    if (aUncharacterized !== bUncharacterized) {
      return aUncharacterized ? 1 : -1;
    }
    const byStream = streamKeyFor(a.wasteStream?.name, a.wasteStream?.id ?? null).localeCompare(
      streamKeyFor(b.wasteStream?.name, b.wasteStream?.id ?? null)
    );
    if (byStream !== 0) {
      return byStream;
    }
    return a.container.containerLabel.localeCompare(b.container.containerLabel);
  });
}

/**
 * What a stream's group row states about the containers beneath it.
 *
 * Deliberately not MUI's aggregation: a shipment can hold containers measured
 * in different units, which is why totals are per unit. `totalsByUnitFor` is
 * the one place that knows it — a built-in `sum` would add gallons to pounds
 * and render a confident wrong number.
 */
export type StreamRollup = {
  name: string;
  containerCount: number;
  totals: Array<{ unit: UnitType; total: number }>;
  issueCount: number;
  blocked: boolean;
  /**
   * The stream's own classification, which every container under it shares —
   * these are snapshots taken off the waste stream, not per-container facts.
   * They belong on the group row for the same reason the previous layout put
   * them in the stream header: rendering them on each container repeated the
   * same three values down the table and left the heading with nothing on it.
   */
  wasteCodes: string;
  dotDescription: string | null;
  profileNumber: string | null;
  /**
   * Hazardous / non-hazardous, as a chip beside the name. Both container
   * pickers say this on their stream headings, and it is the fact that decides
   * how the load is handled — the contents were the one place it went missing.
   */
  classification: WasteClassification | null;
  /** The real stream id, which is how the manifest comparison is keyed. */
  streamId: string | null;
};
